import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  User,
  Mail,
  Phone,
  BookOpen,
  Layers,
  CreditCard,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Award,
  Calendar,
  Globe,
  Building,
  ShieldCheck,
  ShieldAlert,
  Trash2,
} from "lucide-react";
import Button from "../../../components/common/Button";
import { AdminEnrollmentItem } from "../../../services/enrollmentService";
import {
  useUpdateAdminEnrollmentMutation,
  useDeleteAdminEnrollmentMutation,
} from "../../../hooks/queries/useEnrollmentAdminQueries";
import { useToast } from "../../../hooks/otherHooks/useToast";

interface EnrollmentDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  enrollment: AdminEnrollmentItem | null;
  onSuccess: () => void;
}

const EnrollmentDetailModal: React.FC<EnrollmentDetailModalProps> = ({
  isOpen,
  onClose,
  enrollment,
  onSuccess,
}) => {
  const { addToast } = useToast();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { mutateAsync: updateEnrollmentMutation, isPending: isUpdating } =
    useUpdateAdminEnrollmentMutation();
  const { mutateAsync: deleteEnrollmentMutation, isPending: isDeleting } =
    useDeleteAdminEnrollmentMutation();

  if (!isOpen || !enrollment) return null;

  const handleToggleAccess = async () => {
    try {
      await updateEnrollmentMutation({
        id: enrollment.id,
        mode: enrollment.delivery_mode,
        data: { is_enrolled: !enrollment.is_enrolled },
      });
      addToast(
        `Curriculum access ${!enrollment.is_enrolled ? "granted" : "suspended"} successfully!`,
        "success"
      );
      onSuccess();
    } catch (err: any) {
      console.error("Failed to toggle access:", err);
      addToast("Failed to update access state.", "error");
    }
  };

  const handleTogglePaid = async () => {
    try {
      await updateEnrollmentMutation({
        id: enrollment.id,
        mode: enrollment.delivery_mode,
        data: {
          is_paid: !enrollment.is_paid,
          payment_amount: !enrollment.is_paid
            ? enrollment.payment_amount ||
              (enrollment.delivery_mode === "online"
                ? enrollment.curriculum?.online_price || 0
                : enrollment.curriculum?.onsite_price || 0)
            : enrollment.payment_amount,
          payment_method: !enrollment.is_paid ? "Admin Override" : enrollment.payment_method,
        },
      });
      addToast(
        `Tuition marked as ${!enrollment.is_paid ? "paid" : "unpaid"} successfully!`,
        "success"
      );
      onSuccess();
    } catch (err: any) {
      console.error("Failed to toggle payment status:", err);
      addToast("Failed to update tuition state.", "error");
    }
  };

  const handleToggleGraduation = async () => {
    try {
      await updateEnrollmentMutation({
        id: enrollment.id,
        mode: enrollment.delivery_mode,
        data: {
          is_completed: !enrollment.is_completed,
          progress_percent: !enrollment.is_completed ? 100 : enrollment.progress_percent,
        },
      });
      addToast(
        `Academic status updated: student marked as ${
          !enrollment.is_completed ? "Graduated / Completed" : "In Progress"
        }!`,
        "success"
      );
      onSuccess();
    } catch (err: any) {
      console.error("Failed to toggle graduation status:", err);
      addToast("Failed to update graduation state.", "error");
    }
  };

  const handleDeleteEnrollment = async () => {
    try {
      await deleteEnrollmentMutation({
        id: enrollment.id,
        mode: enrollment.delivery_mode,
      });
      addToast("Enrollment admission record deleted successfully.", "success");
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Failed to delete enrollment:", err);
      addToast("Failed to remove enrollment record.", "error");
    }
  };

  return (
    <AnimatePresence>
      <div className="cht-fixed cht-inset-0 cht-z-50 cht-flex cht-items-center cht-justify-center cht-p-4 cht-bg-black/50 cht-backdrop-blur-sm cht-overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="cht-w-full cht-max-w-2xl cht-bg-white cht-rounded-3xl cht-shadow-2xl cht-border cht-border-gray-100 cht-overflow-hidden cht-my-8"
        >
          {/* Header */}
          <div className="cht-px-8 cht-py-6 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between cht-bg-gray-50/50">
            <div className="cht-flex cht-items-center cht-gap-3">
              <div
                className={`cht-w-10 cht-h-10 cht-rounded-xl cht-flex cht-items-center cht-justify-center ${
                  enrollment.delivery_mode === "online"
                    ? "cht-bg-blue-50 cht-text-blue-600"
                    : "cht-bg-emerald-50 cht-text-emerald-600"
                }`}
              >
                {enrollment.delivery_mode === "online" ? <Globe size={20} /> : <Building size={20} />}
              </div>
              <div>
                <div className="cht-flex cht-items-center cht-gap-2">
                  <h3 className="cht-text-lg cht-font-black cht-text-gray-900 cht-tracking-tight">
                    Student Admissions Dossier
                  </h3>
                  <span
                    className={`cht-text-[10px] cht-font-black cht-uppercase cht-px-2.5 cht-py-0.5 cht-rounded-full ${
                      enrollment.delivery_mode === "online"
                        ? "cht-bg-blue-50 cht-text-blue-700"
                        : "cht-bg-emerald-50 cht-text-emerald-700"
                    }`}
                  >
                    {enrollment.delivery_mode} Learning
                  </span>
                </div>
                <p className="cht-text-xs cht-text-gray-500 cht-font-medium">
                  Enrollment ID: #{enrollment.id} • Registered on {enrollment.date}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="cht-p-2 cht-text-gray-400 hover:cht-text-gray-600 cht-rounded-full hover:cht-bg-gray-100 cht-transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <div className="cht-p-8 cht-space-y-6">
            {/* Student & Curriculum Cards Grid */}
            <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 cht-gap-4">
              {/* Student Profile Card */}
              <div className="cht-p-5 cht-rounded-2xl cht-bg-gray-50/70 cht-border cht-border-gray-100 cht-space-y-3">
                <div className="cht-flex cht-items-center cht-gap-3">
                  <img
                    src={enrollment.student_details?.photo_url || "https://cdn-icons-png.flaticon.com/512/147/147142.png"}
                    alt={enrollment.student_details?.name}
                    className="cht-w-12 cht-h-12 cht-rounded-full cht-object-cover cht-border cht-border-white cht-shadow-sm"
                  />
                  <div>
                    <h4 className="cht-text-sm cht-font-black cht-text-gray-900">
                      {enrollment.student_details?.name}
                    </h4>
                    <p className="cht-text-xs cht-text-gray-500 cht-font-medium">
                      {enrollment.student_details?.email}
                    </p>
                  </div>
                </div>

                <div className="cht-space-y-1.5 cht-pt-2 cht-border-t cht-border-gray-200/60 cht-text-xs cht-text-gray-600">
                  {enrollment.student_details?.phone_number && (
                    <div className="cht-flex cht-items-center cht-gap-2">
                      <Phone size={13} className="cht-text-gray-400" />
                      <span>{enrollment.student_details.phone_number}</span>
                    </div>
                  )}
                  {enrollment.student_details?.address && (
                    <p className="cht-text-[11px] cht-text-gray-400">
                      Address: {enrollment.student_details.address}
                    </p>
                  )}
                </div>
              </div>

              {/* Curriculum Details Card */}
              <div className="cht-p-5 cht-rounded-2xl cht-bg-gray-50/70 cht-border cht-border-gray-100 cht-space-y-3">
                <div className="cht-flex cht-items-start cht-gap-3">
                  {enrollment.curriculum?.image_url && (
                    <img
                      src={enrollment.curriculum.image_url}
                      alt={enrollment.curriculum.title}
                      className="cht-w-12 cht-h-12 cht-rounded-xl cht-object-cover cht-border cht-border-white cht-shadow-sm flex-shrink-0"
                    />
                  )}
                  <div className="cht-space-y-0.5">
                    <span className="cht-text-[9px] cht-font-black cht-text-[#3e57da] cht-uppercase cht-tracking-wider">
                      {enrollment.curriculum?.type === "course" ? "Master Course" : "Career Track"}
                    </span>
                    <h4 className="cht-text-xs cht-font-black cht-text-gray-900 cht-line-clamp-2">
                      {enrollment.curriculum?.title}
                    </h4>
                    <p className="cht-text-[10px] cht-text-gray-400">
                      Lead Faculty: {enrollment.curriculum?.instructor_name || "Codehub Faculty"}
                    </p>
                  </div>
                </div>

                <div className="cht-flex cht-items-center cht-justify-between cht-pt-2 cht-border-t cht-border-gray-200/60 cht-text-xs">
                  <span className="cht-text-[11px] cht-font-medium cht-text-gray-500">Catalog Price:</span>
                  <span className="cht-text-xs cht-font-black cht-text-gray-900">
                    ₦
                    {(enrollment.delivery_mode === "online"
                      ? enrollment.curriculum?.online_price
                      : enrollment.curriculum?.onsite_price
                    )?.toLocaleString() || "0"}
                  </span>
                </div>
              </div>
            </div>

            {/* Academic Progression & Learning Telemetry */}
            <div className="cht-p-5 cht-rounded-2xl cht-bg-white cht-border cht-border-gray-200/80 cht-shadow-sm cht-space-y-4">
              <div className="cht-flex cht-items-center cht-justify-between">
                <div className="cht-flex cht-items-center cht-gap-2">
                  <Award size={16} className="cht-text-[#3e57da]" />
                  <h4 className="cht-text-xs cht-font-black cht-text-gray-900 cht-uppercase cht-tracking-wider">
                    Academic Progression
                  </h4>
                </div>
                <div className="cht-flex cht-items-center cht-gap-2">
                  {enrollment.is_completed ? (
                    <span className="cht-px-2.5 cht-py-0.5 cht-bg-purple-50 cht-text-purple-700 cht-rounded-full cht-text-[10px] cht-font-black cht-flex cht-items-center cht-gap-1">
                      <Award size={12} /> Graduated
                    </span>
                  ) : (
                    <span className="cht-px-2.5 cht-py-0.5 cht-bg-blue-50 cht-text-blue-700 cht-rounded-full cht-text-[10px] cht-font-black">
                      In Progress
                    </span>
                  )}
                  <span className="cht-text-sm cht-font-black cht-text-gray-900">
                    {enrollment.progress_percent}%
                  </span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="cht-w-full cht-h-2.5 cht-bg-gray-100 cht-rounded-full cht-overflow-hidden">
                <div
                  style={{ width: `${enrollment.progress_percent}%` }}
                  className={`cht-h-full cht-rounded-full cht-transition-all cht-duration-500 ${
                    enrollment.is_completed
                      ? "cht-bg-purple-600"
                      : enrollment.progress_percent > 50
                      ? "cht-bg-[#3e57da]"
                      : "cht-bg-amber-500"
                  }`}
                />
              </div>

              <div className="cht-grid cht-grid-cols-2 sm:cht-grid-cols-3 cht-gap-3 cht-pt-2 cht-text-xs">
                <div>
                  <p className="cht-text-[10px] cht-font-bold cht-text-gray-400">Completed Lessons</p>
                  <p className="cht-font-black cht-text-gray-900">
                    {enrollment.completed_lessons?.length || 0} of {enrollment.total_lessons_count || "—"}
                  </p>
                </div>
                <div>
                  <p className="cht-text-[10px] cht-font-bold cht-text-gray-400">Last Platform Active</p>
                  <p className="cht-font-black cht-text-gray-900">
                    {enrollment.last_active_date || "Not active yet"}
                  </p>
                </div>
                <div>
                  <p className="cht-text-[10px] cht-font-bold cht-text-gray-400">Certificate Status</p>
                  <p className="cht-font-black cht-text-emerald-600">
                    {enrollment.is_completed && enrollment.is_paid ? "Verified & Ready" : "Pending completion"}
                  </p>
                </div>
              </div>
            </div>

            {/* Tuition Settlement Status */}
            <div className="cht-p-5 cht-rounded-2xl cht-bg-gray-50/70 cht-border cht-border-gray-100 cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-4">
              <div className="cht-space-y-1">
                <div className="cht-flex cht-items-center cht-gap-2">
                  <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                    Tuition Status:
                  </span>
                  <span
                    className={`cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black ${
                      enrollment.is_paid
                        ? "cht-bg-emerald-50 cht-text-emerald-700"
                        : "cht-bg-amber-50 cht-text-amber-700"
                    }`}
                  >
                    {enrollment.is_paid ? "PAID / CLEARED" : "PENDING TUITION"}
                  </span>
                </div>
                <p className="cht-text-lg cht-font-black cht-text-gray-900">
                  ₦{Number(enrollment.payment_amount || 0).toLocaleString()}
                </p>
                <p className="cht-text-[10px] cht-text-gray-400">
                  Channel: {enrollment.payment_method || "Direct Intake"} • Date:{" "}
                  {enrollment.payment_date ? enrollment.payment_date.slice(0, 10) : "Awaiting payment"}
                </p>
              </div>

              <div className="cht-flex cht-flex-wrap cht-gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleTogglePaid}
                  disabled={isUpdating}
                  className="cht-rounded-xl cht-text-xs cht-font-bold"
                >
                  {enrollment.is_paid ? "Mark as Unpaid" : "Mark as Paid"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleToggleAccess}
                  disabled={isUpdating}
                  className="cht-rounded-xl cht-text-xs cht-font-bold"
                >
                  {enrollment.is_enrolled ? "Suspend Access" : "Grant Access"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleToggleGraduation}
                  disabled={isUpdating}
                  className="cht-rounded-xl cht-text-xs cht-font-bold"
                >
                  {enrollment.is_completed ? "Reset Progress" : "Mark Graduated"}
                </Button>
              </div>
            </div>

            {/* Certificate Verification Direct Link (if completed & paid) */}
            {enrollment.is_completed && enrollment.is_paid && (
              <div className="cht-p-4 cht-rounded-2xl cht-bg-purple-50/60 cht-border cht-border-purple-200/60 cht-flex cht-items-center cht-justify-between">
                <div className="cht-flex cht-items-center cht-gap-3">
                  <div className="cht-w-8 cht-h-8 cht-rounded-lg cht-bg-purple-600 cht-text-white cht-flex cht-items-center cht-justify-center">
                    <Award size={16} />
                  </div>
                  <div>
                    <p className="cht-text-xs cht-font-black cht-text-purple-900">
                      Official Certificate ID: CH-{enrollment.id}-CS-2026
                    </p>
                    <p className="cht-text-[10px] cht-text-purple-700">
                      Eligible for public employer verification and digital badge download
                    </p>
                  </div>
                </div>
                <Button
                  variant="primary"
                  onClick={() =>
                    window.open(
                      `/verify-certificate?cert_id=CH-${enrollment.id}-CS-2026`,
                      "_blank"
                    )
                  }
                  className="cht-bg-purple-600 hover:cht-bg-purple-700 cht-rounded-xl cht-text-xs cht-font-bold cht-py-2 cht-px-3"
                  leftIcon={<ExternalLink size={13} />}
                >
                  Verify
                </Button>
              </div>
            )}

            {/* Danger Zone: Delete / Unenroll */}
            <div className="cht-pt-4 cht-border-t cht-border-gray-100 cht-flex cht-items-center cht-justify-between">
              {!confirmDelete ? (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="cht-text-rose-500 hover:cht-text-rose-600 cht-text-xs cht-font-bold cht-flex cht-items-center cht-gap-1.5 cht-transition-colors"
                >
                  <Trash2 size={14} /> Remove Enrollment Record
                </button>
              ) : (
                <div className="cht-flex cht-items-center cht-gap-2">
                  <span className="cht-text-xs cht-font-bold cht-text-rose-600">
                    Are you sure? This cannot be undone:
                  </span>
                  <button
                    type="button"
                    onClick={handleDeleteEnrollment}
                    disabled={isDeleting}
                    className="cht-px-3 cht-py-1.5 cht-rounded-lg cht-bg-rose-600 cht-text-white cht-text-xs cht-font-bold hover:cht-bg-rose-700"
                  >
                    {isDeleting ? "Deleting..." : "Yes, Delete Record"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="cht-px-3 cht-py-1.5 cht-rounded-lg cht-bg-gray-100 cht-text-gray-700 cht-text-xs cht-font-bold hover:cht-bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              )}

              <Button
                type="button"
                variant="primary"
                onClick={onClose}
                className="cht-bg-[#1e293b] hover:cht-bg-gray-800 cht-rounded-xl cht-text-xs cht-font-bold cht-px-6"
              >
                Close Dossier
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default EnrollmentDetailModal;
